import React, { Fragment } from 'react'
import { connect } from 'react-redux'

import lang from 'lang'
import config from 'config'
import request from '@/modules/base/util/request'
import download from 'downloadjs'
import { makeStyles } from '@mui/styles'
import DatevService from '@/modules/apps/own/datev/datevService'
import { openEmailModal, openFixErrorsDrawer } from '@/redux/ducks/documentExport/list'
import { ButtonSquare } from '@/modules/base/components/buttons/index'
import { useOpenModal } from '@/modules/_helpers/openModal'
import { toast } from '@/modules/base/components/toast/toastConfig'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'

const getFileNameFromDisposition = (contentDisposition) => {
  if (!contentDisposition) {
    return undefined
  }

  const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i)
  if (!fileNameMatch?.[1]) {
    return undefined
  }

  try {
    return decodeURIComponent(fileNameMatch[1].replace(/^"|"$/g, ''))
  } catch {
    return fileNameMatch[1].replace(/^"|"$/g, '')
  }
}

const FAILED_ERROR_CATEGORIES = {
  failedExpensesErrors: 'expenses',
  failedRevenuesErrors: 'revenues',
  failedInvoicesErrors: 'invoices',
  failedOthersErrors: 'others',
}

const collectFailedDocuments = (protocol) => Object.entries(FAILED_ERROR_CATEGORIES)
  .flatMap(([key, category]) => (protocol[key] ?? []).map((item) => ({ ...item, category })))

const ActionCell = (props) => {
  const classes = useStyles()
  const openModal = useOpenModal()
  const {
    collaborationId,
    openFixErrorsDrawer,
    data: { status, documentUrl, contentType, id, exportType },
  } = props

  const handleDownloadClick = () => {
    const headers = {}
    if (collaborationId) {
      headers['collaboration-id'] = collaborationId
    }

    request(`${config.assetResourceHost}${documentUrl}`, {
      method: 'GET',
      auth: true,
      headers,
      responseType: 'arraybuffer',
    })
      .then((response) => {
        const responseContentType = response.response?.headers?.['content-type'] || contentType || 'application/octet-stream'
        const fileName = response.response?.headers?.filename
          || getFileNameFromDisposition(response.response?.headers?.['content-disposition'])

        if (responseContentType === 'application/pdf') {
          const blob = new Blob([response.body], { type: responseContentType })
          const blobUrl = URL.createObjectURL(blob)
          window.open(blobUrl, '_blank')
          window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
          return
        }

        download(response.body, fileName, responseContentType)
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      })
  }

  const openProtocol = async(e) => {

    if (e.ctrlKey) {
      DatevService.downloadTransferredDocument(id)
      return
    }

    const protocol = await DatevService.getProtocol(id)
    if (!protocol) {
      return
    }

    const protocolString = JSON.stringify(protocol, null, 2)
    const failedDocuments = collectFailedDocuments(protocol)

    openModal(close => ({
      component: 'ModalBig',
      props: {
        title: lang.documentExport.transferProtocol,
        onClose: close,
        actions: [
          <div style={{ marginBottom: -20 + 'px', display: 'flex', gap: 10 + 'px' }}>
            <ButtonIconSquare
              onClick={close}
              width='100%'
              label={lang.closeButtonLabel}
              variant={failedDocuments.length > 0 ? 'outline' : 'fill'}
              dataQsId='datev-protocol-close-button'
            />
            {failedDocuments.length > 0 && (
              <ButtonSquare
                onClick={() => {
                  close()
                  openFixErrorsDrawer(failedDocuments)
                }}
                label={lang.documentExport.fixErrors.fixNowButton}
                variant={'small'}
                dataQsId="datev-protocol-fix-errors-open"
              />
            )}
          </div>,
        ],
      },
      children: [
        <div key="transfer-protocol" style={{ whiteSpace: 'break-spaces' }}>
          {protocolString}
        </div>,
      ],
    }))
  }

  let content
  switch (status) {
    case 'success': {
      content = (
        <Fragment>
          { exportType !== 'DATEV_ONLINE'
            ? <span className={classes.action} onClick={handleDownloadClick}>{lang.generics.download}</span>
            : <span className={classes.action} onClick={openProtocol}>{lang.generics.success}</span>
          }

        </Fragment>
      )
      break
    }
    case 'pending': {
      content = <span>{lang.documentExport.willBeCreated}</span>
      break
    }
    case 'error': {
      content = <span className={[classes.action]} onClick={openProtocol}>{lang.generics.failed}</span>
      break
    }
    case 'partial_error': {
      content = <span className={[classes.action]} onClick={openProtocol}>{lang.generics.partially_success}</span>
      break
    }
  }

  return (
    <div className={classes.container}>
      {content}
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    '& span': {
      lineHeight: '18px',
    },
  },
  action: {
    cursor: 'pointer',
    color: theme.primary,
  },
  error: {
    color: theme.error,
  },
}))

const mapDispatchToProps = (dispatch) => ({
  openEmailModal: (documentExport) => dispatch(openEmailModal(documentExport)),
  openFixErrorsDrawer: (failedDocuments) => dispatch(openFixErrorsDrawer(failedDocuments)),
})

export default connect(null, mapDispatchToProps)(ActionCell)
